<template>
  <div class="wechat-login-page">
    <el-card class="setting-card" :loading="loading">
      <template #header>
        <div class="card-header">
          <span>微信登录设置</span>
          <div class="header-actions">
            <el-button size="small" @click="resetToEnv" :loading="loading">恢复默认</el-button>
            <el-button size="small" type="warning" @click="testConnection" :loading="testing">测试连接</el-button>
            <el-button size="small" type="primary" @click="save" :loading="saving">保存</el-button>
          </div>
        </div>
      </template>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="160px" class="setting-form">
        <el-form-item label="启用小程序登录" prop="enable_mp_login">
          <el-switch v-model="form.enable_mp_login" />
        </el-form-item>
        <el-form-item label="启用网站扫码登录" prop="enable_web_login">
          <el-switch v-model="form.enable_web_login" />
        </el-form-item>
        <el-divider content-position="left">小程序配置</el-divider>
        <el-form-item label="小程序 AppID" prop="wechat_mp_appid">
          <el-input v-model="form.wechat_mp_appid" placeholder="请输入小程序 AppID" clearable />
        </el-form-item>
        <el-form-item label="小程序 Secret" prop="wechat_mp_secret">
          <div class="secret-row">
            <el-input
              v-model="mpSecret"
              :placeholder="mpSecretConfigured ? '已配置，留空则保持不变' : '请输入小程序 Secret'"
              show-password
              clearable
            />
            <el-button v-if="mpSecretConfigured" text type="danger" @click="clearMpSecret">清除</el-button>
          </div>
        </el-form-item>
        <el-divider content-position="left">网站扫码配置</el-divider>
        <el-form-item label="开放平台 AppID" prop="wechat_open_appid">
          <el-input v-model="form.wechat_open_appid" placeholder="请输入开放平台 AppID" clearable />
        </el-form-item>
        <el-form-item label="开放平台 Secret" prop="wechat_open_secret">
          <div class="secret-row">
            <el-input
              v-model="openSecret"
              :placeholder="openSecretConfigured ? '已配置，留空则保持不变' : '请输入开放平台 Secret'"
              show-password
              clearable
            />
            <el-button v-if="openSecretConfigured" text type="danger" @click="clearOpenSecret">清除</el-button>
          </div>
        </el-form-item>
        <el-form-item label="网站回调 Redirect URI" prop="wechat_open_redirect_uri">
          <el-input v-model="form.wechat_open_redirect_uri" placeholder="请输入 HTTPS Redirect URI" clearable />
        </el-form-item>
        <el-form-item label="扫码成功跳转" prop="frontend_qr_success_url">
          <el-input v-model="form.frontend_qr_success_url" placeholder="扫码成功后跳转地址(可选)" clearable />
        </el-form-item>
      </el-form>
      <el-alert
        v-if="dirty"
        title="当前存在未保存的修改"
        type="warning"
        show-icon
        class="unsaved-alert"
      />
    </el-card>
  </div>
</template>

<script setup>
import { reactive, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'

const SECRET_PLACEHOLDER = '__CONFIGURED__'

const formRef = ref()
const loading = ref(false)
const saving = ref(false)
const testing = ref(false)
const dirty = ref(false)
const ready = ref(false)
const form = reactive({
  enable_mp_login: false,
  enable_web_login: false,
  wechat_mp_appid: '',
  wechat_open_appid: '',
  wechat_open_redirect_uri: '',
  frontend_qr_success_url: ''
})
const mpSecret = ref('')
const openSecret = ref('')
const mpSecretConfigured = ref(false)
const openSecretConfigured = ref(false)
let savedSnapshot = ''

const rules = {
  wechat_mp_appid: [{ required: true, message: '请输入小程序 AppID', trigger: 'blur' }],
  wechat_open_appid: [{ required: true, message: '请输入开放平台 AppID', trigger: 'blur' }],
  wechat_open_redirect_uri: [
    {
      validator: (_, value, callback) => {
        if (!value) {
          callback(new Error('请输入 Redirect URI'))
        } else if (!/^https:\/\//i.test(value)) {
          callback(new Error('Redirect URI 必须为 HTTPS'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

const computeSnapshot = () => {
  return JSON.stringify({
    enable_mp_login: form.enable_mp_login,
    enable_web_login: form.enable_web_login,
    wechat_mp_appid: form.wechat_mp_appid,
    wechat_open_appid: form.wechat_open_appid,
    wechat_open_redirect_uri: form.wechat_open_redirect_uri,
    frontend_qr_success_url: form.frontend_qr_success_url,
    mpSecret: mpSecret.value,
    mpSecretConfigured: mpSecretConfigured.value,
    openSecret: openSecret.value,
    openSecretConfigured: openSecretConfigured.value
  })
}

const syncDirty = () => {
  if (!ready.value) return
  dirty.value = computeSnapshot() !== savedSnapshot
}

watch(
  () => [
    form.enable_mp_login,
    form.enable_web_login,
    form.wechat_mp_appid,
    form.wechat_open_appid,
    form.wechat_open_redirect_uri,
    form.frontend_qr_success_url,
    mpSecret.value,
    mpSecretConfigured.value,
    openSecret.value,
    openSecretConfigured.value
  ],
  syncDirty
)

const applyData = async (data, markSaved) => {
  const payload = data && typeof data === 'object' ? (data.data ?? data) : {}
  form.enable_mp_login = Boolean(payload.enable_mp_login)
  form.enable_web_login = Boolean(payload.enable_web_login)
  form.wechat_mp_appid = payload.wechat_mp_appid || ''
  form.wechat_open_appid = payload.wechat_open_appid || ''
  form.wechat_open_redirect_uri = payload.wechat_open_redirect_uri || ''
  form.frontend_qr_success_url = payload.frontend_qr_success_url || ''
  mpSecretConfigured.value = Boolean(payload.wechat_mp_secret_configured)
  openSecretConfigured.value = Boolean(payload.wechat_open_secret_configured)
  mpSecret.value = mpSecretConfigured.value ? '' : (payload.wechat_mp_secret || '')
  openSecret.value = openSecretConfigured.value ? '' : (payload.wechat_open_secret || '')
  await nextTick()
  const snap = computeSnapshot()
  if (markSaved) {
    savedSnapshot = snap
  }
  dirty.value = snap !== savedSnapshot
}

const loadSettings = async ({ useEnv = false, markSaved = !useEnv } = {}) => {
  loading.value = true
  ready.value = false
  try {
    const options = useEnv ? { params: { source: 'env' } } : undefined
    const data = await request.get('/v1/api/admin/wechat-login/settings', options)
    await applyData(data, markSaved)
  } catch (err) {
    ElMessage.error(err.toString())
  } finally {
    ready.value = true
    loading.value = false
  }
}

const save = async () => {
  if (!formRef.value) return
  try {
    await formRef.value.validate()
  } catch (err) {
    return
  }
  saving.value = true
  try {
    const payload = {
      enable_mp_login: form.enable_mp_login,
      enable_web_login: form.enable_web_login,
      wechat_mp_appid: form.wechat_mp_appid.trim(),
      wechat_open_appid: form.wechat_open_appid.trim(),
      wechat_open_redirect_uri: form.wechat_open_redirect_uri.trim(),
      frontend_qr_success_url: form.frontend_qr_success_url.trim(),
      wechat_mp_secret:
        mpSecretConfigured.value && mpSecret.value === '' ? SECRET_PLACEHOLDER : mpSecret.value.trim(),
      wechat_open_secret:
        openSecretConfigured.value && openSecret.value === '' ? SECRET_PLACEHOLDER : openSecret.value.trim()
    }
    await request.put('/v1/api/admin/wechat-login/settings', payload)
    ElMessage.success('保存成功')
    await loadSettings()
  } catch (err) {
    ElMessage.error(err.toString())
  } finally {
    saving.value = false
  }
}

const testConnection = async () => {
  testing.value = true
  try {
    const res = await request.post('/v1/api/admin/wechat-login/test')
    const message = (res && res.message) || '测试通过'
    ElMessage.success(message)
  } catch (err) {
    ElMessage.error(err.toString())
  } finally {
    testing.value = false
  }
}

const resetToEnv = async () => {
  await loadSettings({ useEnv: true, markSaved: false })
  ElMessage.info('已加载环境变量，请确认后保存覆盖现有配置')
}

const clearMpSecret = () => {
  mpSecretConfigured.value = false
  mpSecret.value = ''
}

const clearOpenSecret = () => {
  openSecretConfigured.value = false
  openSecret.value = ''
}

const handleBeforeUnload = (event) => {
  if (!dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => {
  loadSettings()
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

onBeforeRouteLeave((to, from, next) => {
  if (!dirty.value) {
    next()
  } else {
    ElMessageBox.confirm('当前修改尚未保存，确定离开吗？', '提示', {
      type: 'warning'
    })
      .then(() => next())
      .catch(() => next(false))
  }
})
</script>

<style scoped>
.wechat-login-page {
  padding: 16px;
}
.setting-card {
  max-width: 720px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.secret-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.unsaved-alert {
  margin-top: 16px;
}
</style>
